import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAnimatedList } from '../../../../hooks/useAnimatedList';
import { useHiddenEvents } from '../../../../stores/appStore';
import { useStableStyle } from '../../../../hooks/useStableStyle';
import './AnimatedList.css';

// 🔧 FIX PRD Navigation : Clé sessionStorage pour tracker si la liste a déjà été affichée
const SESSION_KEY = 'animatedList_initialized';

interface AnimatedListProps {
  children: React.ReactNode[];
}

const AnimatedList: React.FC<AnimatedListProps> = ({ children }) => {
  const { isHidden, getExitReason } = useHiddenEvents();

  // 📱 Détection mobile pour ajuster la vitesse d'animation
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches;

  // 🔧 FIX PRD Navigation : Vérifier si c'est le vrai premier affichage de la session
  // Si on revient d'une modale (navigation back), on skip l'animation d'entrée
  const [isFirstSessionRender] = useState(() => {
    const wasInitialized = sessionStorage.getItem(SESSION_KEY);
    if (!wasInitialized) {
      // Marquer comme initialisé pour les prochains renders/remounts
      sessionStorage.setItem(SESSION_KEY, 'true');
      return true;
    }
    return false;
  });

  // 🚀 OPTIMISATION : Props mémorisés pour éviter re-renders
  const itemStyle = useStableStyle({
    position: 'relative',
    overflow: 'visible' // Permet l'affichage complet des cartes
  }, []);

  // 🚀 OPTIMISATION : Props d'animation mémorisés
  // 🔧 FIX PRD Navigation : Animation seulement au premier affichage de la session
  // Quand on revient d'une modale (remount du composant), pas d'animation
  // 📱 Mobile : animation plus rapide (0.15s) pour une meilleure réactivité
  const layoutDuration = isMobile ? 0.2 : 0.3;
  const animationProps = useMemo(() => ({
    initial: isFirstSessionRender ? { height: 0, opacity: 0 } : false,
    animate: { height: "auto", opacity: 1 },
    exit: { height: 0, opacity: 0 },
    transition: {
      layout: {
        type: "spring" as const,
        bounce: 0,
        duration: layoutDuration
      },
      height: { duration: isFirstSessionRender ? 0.4 : 0.2, ease: "easeInOut" as const },
      opacity: { duration: isFirstSessionRender ? 0.3 : 0.15 }
    }
  }), [isFirstSessionRender, layoutDuration]);

  // Utilisons l'ID approprié selon le type d'événement
  const items = React.Children.map(children, (child, index) => {
    // FIX: Ignorer les children null/undefined/false (rendus conditionnels)
    if (!React.isValidElement(child)) return null;

    const eventProps = child.props as Record<string, any>;
    let eventId;
    if (eventProps.mainEvent) {
      eventId = eventProps.mainEvent.seriesId || eventProps.mainEvent.id;
    } else if (eventProps.event) {
      eventId = eventProps.event.id;
    } else {
      eventId = `non-event-${index}`;
    }
    return {
      id: eventId,
      content: child,
      isHidden: isHidden(eventId),
      exitReason: getExitReason(eventId)
    };
  })?.filter(Boolean) || [];

  // 🔍 Filtrer les éléments masqués
  const visibleItems = items.filter(item => !item.isHidden);

  const { animatedItems } = useAnimatedList(
    visibleItems.map(item => ({
      ...item,
      exitReason: item.exitReason
    })),
    {
      exitDuration: 300
    }
  );

  // 🔄 Forcer la mise à jour quand les événements masqués changent
  useEffect(() => {
    const handleHiddenEventsUpdate = () => {
      console.log(' AnimatedList: Mise à jour suite aux événements masqués');
    };

    window.addEventListener('hiddenEventsUpdated', handleHiddenEventsUpdate);
    return () => {
      window.removeEventListener('hiddenEventsUpdated', handleHiddenEventsUpdate);
    };
  }, []);

  return (
    <motion.div className="animated-list">
      <AnimatePresence initial={false}>
        {animatedItems.map((item) => (
          <motion.div
            key={item.id}
            layout
            {...animationProps}
            style={itemStyle}
          >
            {item.data.content}
          </motion.div>
        ))}
      </AnimatePresence>
      <div className="list-bottom-spacer" aria-hidden="true" />
    </motion.div>
  );
}; 

export { AnimatedList };